/**
 * A tile's trend: one thin line, no axes, the same days as every other sparkline in its row (the
 * caller aligns them), so a row compares like with like. Decorative: the tile states the figure
 * and its change in words. With no data it is a dash, never a flat line that reads as zero. A last
 * point that is a day still going (today so far) is joined by a dashed step.
 */

import { linePath, linear, maxOf, positions } from "./scale";

const W = 120;
const H = 32;

export function Sparkline({ values, className, fill, partialLast }: {
  values: ReadonlyArray<number | null>;
  className?: string;
  /** Take the container's height instead of a tile's 32 px. */
  fill?: boolean;
  /** The last value is a period still going (today so far). */
  partialLast?: boolean;
}) {
  const hasData = values.some((v) => typeof v === "number" && v > 0);
  if (!hasData) return <span className={`text-body-2 text-t-tertiary ${className || ""}`} aria-hidden="true">—</span>;
  const xs = positions(values.length, 2, W - 2);
  const y = linear(0, maxOf(values), H - 2, 2);
  const ys = values.map((v) => (typeof v === "number" ? y(v) : null));
  const dashLast = partialLast && ys.length > 1;
  const line = { fill: "none", strokeWidth: 2, strokeLinejoin: "round", strokeLinecap: "round", vectorEffect: "non-scaling-stroke", className: "stroke-accent-stroke" } as const;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className={`block w-full ${fill ? "h-full min-h-8" : "h-8"} ${className || ""}`} aria-hidden="true" focusable="false">
      <path d={linePath(xs, dashLast ? ys.map((v, i) => (i === ys.length - 1 ? null : v)) : ys)} {...line} />
      {dashLast ? <path d={linePath(xs, ys.map((v, i) => (i >= ys.length - 2 ? v : null)))} {...line} strokeDasharray="3 3" /> : null}
    </svg>
  );
}
